import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { cn } from "@/shared/lib/utils";
import type {
  AdminCommunication,
  NotificationChannel,
  TestDispatchResult,
  TestDispatchStatus,
} from "../shared/communications-types";
import { RECIPIENT_MAX } from "../shared/communications-types";
import { activeChannels, localesWithActiveTranslation } from "../shared/communication-selectors";
import { parseSampleData } from "../shared/parse-sample-data";
import { resolveCommunicationError } from "../shared/resolve-communication-error";
import { useCommunicationVariables } from "../translations/use-communication-variables";
import { useTestDispatch } from "./use-test-dispatch";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly communication: AdminCommunication;
}

/** Result tone: green for delivered, amber for skipped, red for failed. */
const STATUS_TONE: Record<TestDispatchStatus, string> = {
  SENT: "border-emerald-300 bg-emerald-50 text-emerald-800",
  SKIPPED_SUPPRESSED: "border-amber-300 bg-amber-50 text-amber-800",
  SKIPPED_PROVIDER_OFF: "border-amber-300 bg-amber-50 text-amber-800",
  FAILED: "border-destructive/40 bg-destructive/10 text-destructive",
};

/**
 * Fire an existing catalog communication to a real user over a forced channel
 * for template testing (API-COMM-5). Lives on the communication detail screen.
 *
 * The channel select is limited to ACTIVE channels (the API rejects inactive
 * ones); the locale select offers locales with active copy on that channel; the
 * `data` editor is prefilled from the variable descriptor's `sampleData`. The
 * dispatch outcome (`SENT` / `SKIPPED_*` / `FAILED`) renders inline and the
 * dialog stays open so the operator can read it and retry.
 */
export function TestDispatchDialog({ open, onOpenChange, communication }: Props) {
  const { t } = useTranslation();
  const dispatch = useTestDispatch();
  const variables = useCommunicationVariables(open ? communication.id : undefined);

  const channels = useMemo(() => activeChannels(communication), [communication]);

  const [recipient, setRecipient] = useState("");
  const [channel, setChannel] = useState<NotificationChannel | "">("");
  const [locale, setLocale] = useState("");
  const [dataJson, setDataJson] = useState("{}");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestDispatchResult | null>(null);

  // Reset the form whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setRecipient("");
      setChannel(channels[0]?.channel ?? "");
      setLocale("");
      setError(null);
      setResult(null);
    }
  }, [open, channels]);

  // Seed the data editor from the backend's sample-data descriptor so the
  // {{vars}} interpolate to real values without the operator typing them blind.
  useEffect(() => {
    if (open && variables.data) {
      setDataJson(JSON.stringify(variables.data.sampleData ?? {}, null, 2));
    }
  }, [open, variables.data]);

  const localeOptions = useMemo(() => {
    if (channel === "") return [];
    const selected = channels.find((c) => c.channel === channel) ?? null;
    return localesWithActiveTranslation(selected);
  }, [channels, channel]);

  const canSubmit = recipient.trim() !== "" && channel !== "" && !dispatch.isPending;

  const handleSubmit = async () => {
    // `canSubmit` already guarantees `channel !== ""`, which narrows it to a
    // `NotificationChannel` for the dispatch input below.
    if (!canSubmit) return;
    setError(null);
    setResult(null);

    const parsed = parseSampleData(dataJson);
    if ("error" in parsed) {
      setError(t("communications.testDispatch.dataInvalid"));
      return;
    }

    try {
      const res = await dispatch.mutateAsync({
        communicationId: communication.id,
        input: {
          recipient: recipient.trim(),
          channel,
          ...(locale ? { locale } : {}),
          data: parsed.data,
        },
      });
      setResult(res);
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !dispatch.isPending && onOpenChange(next)}>
      <DialogContent data-testid="communication-test-dispatch-dialog">
        <DialogHeader>
          <DialogTitle>{t("communications.testDispatch.title")}</DialogTitle>
          <DialogDescription>{t("communications.testDispatch.description")}</DialogDescription>
        </DialogHeader>

        {channels.length === 0 ? (
          <p
            data-testid="test-dispatch-no-channels"
            className="rounded-md border border-border bg-muted/30 p-2 text-sm text-muted-foreground"
          >
            {t("communications.testDispatch.noChannels")}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-dispatch-recipient">
                {t("communications.testDispatch.recipientField")}
              </Label>
              <Input
                id="test-dispatch-recipient"
                type="email"
                data-testid="test-dispatch-recipient-input"
                value={recipient}
                maxLength={RECIPIENT_MAX}
                placeholder={t("communications.testDispatch.recipientPlaceholder")}
                onChange={(e) => setRecipient(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="test-dispatch-channel">
                  {t("communications.testDispatch.channelField")}
                </Label>
                <select
                  id="test-dispatch-channel"
                  data-testid="test-dispatch-channel-input"
                  value={channel}
                  onChange={(e) => {
                    setChannel(e.target.value as NotificationChannel);
                    setLocale("");
                  }}
                  className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {channels.map((c) => (
                    <option key={c.id} value={c.channel}>
                      {c.channel}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="test-dispatch-locale">
                  {t("communications.testDispatch.localeField")}
                </Label>
                <select
                  id="test-dispatch-locale"
                  data-testid="test-dispatch-locale-input"
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                  className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t("communications.testDispatch.localeDefault")}</option>
                  {localeOptions.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="test-dispatch-data">
                {t("communications.testDispatch.dataField")}
              </Label>
              <Textarea
                id="test-dispatch-data"
                data-testid="test-dispatch-data-input"
                value={dataJson}
                rows={5}
                spellCheck={false}
                className="font-mono text-xs"
                onChange={(e) => setDataJson(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t("communications.testDispatch.dataHint")}
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              {t("communications.testDispatch.unsavedHint")}
            </p>

            {error && (
              <p data-testid="test-dispatch-error" className="text-sm text-destructive">
                {error}
              </p>
            )}

            {result && (
              <div
                data-testid="test-dispatch-result"
                className={cn(
                  "space-y-1 rounded-md border p-3 text-sm",
                  STATUS_TONE[result.status],
                )}
              >
                <p className="font-medium">
                  {t(`communications.testDispatch.status.${result.status}`, {
                    channel: result.channel,
                  })}
                </p>
                {result.errorMessage && <p className="text-xs">{result.errorMessage}</p>}
                {result.providerMessageId && (
                  <p className="text-xs opacity-80">
                    {t("communications.testDispatch.providerMessageId", {
                      id: result.providerMessageId,
                    })}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={dispatch.isPending}
          >
            {t("communications.actions.cancel")}
          </Button>
          {channels.length > 0 && (
            <Button
              data-testid="test-dispatch-submit-button"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {dispatch.isPending
                ? t("communications.testDispatch.sending")
                : t("communications.testDispatch.send")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
